
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../api";
import {
  FiUser,
  FiMail,
  FiSave,
  FiTrash2,
  FiX,
  FiAlertTriangle,
  FiCalendar,
} from "react-icons/fi";

const AccountSettings = () => {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ firstname: "", lastname: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    let active = true;

    const loadAccount = async () => {
      try {
        const { data } = await API.get("/user/account/settings");

        if (!active) return;

        setUser(data);
        setForm({
          firstname: data.firstname || "",
          lastname: data.lastname || "",
        });
      } catch (err) {
        if (active) {
          setError(err.response?.data?.message || "Unable to load account information.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadAccount();

    return () => {
      active = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!form.firstname.trim() || !form.lastname.trim()) {
      setError("First name and last name are required.");
      return;
    }

    setSaving(true);

    try {
      const { data } = await API.patch("/user/account/settings", {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
      });

      setUser((prev) => ({
        ...prev,
        firstname: data.firstname,
        lastname: data.lastname,
      }));

      setMessage(data.message || "Account information updated.");
    } catch (err) {
      setError(err.response?.data?.message || "Unable to update account.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRequest = async (e) => {
    e.preventDefault();
    setError("");
    setDeleting(true);

    try {
      await API.post("/user/account/deletion", { password });

      setShowDeleteModal(false);
      setPassword("");

      // The backend clears the authentication cookie.
      // A fresh navigation also clears this page's local state.
      navigate("/auth", {replace: true});
    } catch (err) {
      setError(err.response?.data?.message || "Unable to schedule account deletion.");
    } finally {
      setDeleting(false);
    }
  };

  const closeModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setPassword("");
    setError("");
  };

  const formatDate = (date) => {
    if (!date) return "";

    const parsed = new Date(date);

    return Number.isNaN(parsed.getTime())
      ? "Date unavailable"
      : parsed.toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-gray-500">Loading account settings...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12">
        <p className="text-red-600">{error || "Account information unavailable."}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
          <p className="mt-2 text-gray-500">
            Manage your personal information and account preferences.
          </p>
        </div>

        {message && (
          <div role="status" className="rounded-xl bg-green-50 p-4 text-sm text-green-700">
            {message}
          </div>
        )}

        {error && !showDeleteModal && (
          <div role="alert" className="rounded-xl bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-gray-100">
              {user.profile_img ? (
                <img
                  src={user.profile_img}
                  alt="Profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <FiUser className="text-2xl text-gray-500" />
              )}
            </div>

            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {user.firstname} {user.lastname}
              </h2>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>

          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="firstname" className="mb-2 block text-sm font-medium text-gray-700">
                  First name
                </label>
                <input
                  id="firstname"
                  name="firstname"
                  value={form.firstname}
                  onChange={handleChange}
                  maxLength={100}
                  required
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-gray-700"
                />
              </div>

              <div>
                <label htmlFor="lastname" className="mb-2 block text-sm font-medium text-gray-700">
                  Last name
                </label>
                <input
                  id="lastname"
                  name="lastname"
                  value={form.lastname}
                  onChange={handleChange}
                  maxLength={100}
                  required
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-gray-700"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
                <FiMail /> Email address
              </label>
              <input
                type="email"
                value={user.email || ""}
                disabled
                className="w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-100 px-4 py-3 text-gray-500"
              />
              <p className="mt-2 text-xs text-gray-500">
                Email changes are not available from this page yet.
              </p>
            </div>

            <button
              type="submit"
              disabled={saving || !!user.deletion_scheduled_at}
              className="flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-6 py-3 font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiSave />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>

        {user.deletion_scheduled_at && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <div className="flex items-start gap-3">
              <FiCalendar className="mt-1 text-xl text-amber-700" />
              <div>
                <h3 className="font-semibold text-amber-900">Account deletion pending</h3>
                <p className="mt-2 text-sm text-amber-800">
                  Your account is scheduled for permanent deletion on{" "}
                  <strong>{formatDate(user.deletion_scheduled_at)}</strong>.
                  A successful login before the deadline cancels the request.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
          <h2 className="flex items-center gap-2 text-xl font-semibold text-red-600">
            <FiAlertTriangle /> Danger Zone
          </h2>

          <p className="mt-3 text-sm leading-6 text-gray-600">
            Request account deletion if you no longer want to use Alakoze.
            Your account will be scheduled for permanent deletion after 45 days.
            Logging in before the deadline automatically cancels the request.
          </p>

          <button
            type="button"
            disabled={!!user.deletion_scheduled_at}
            onClick={() => {
              setError("");
              setShowDeleteModal(true);
            }}
            className="mt-5 flex items-center gap-2 rounded-xl border border-red-600 px-5 py-3 font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiTrash2 />
            {user.deletion_scheduled_at ? "Deletion Scheduled" : "Delete Account"}
          </button>
        </div>
      </div>

      {showDeleteModal && (
        <div
          role="presentation"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <h2 id="delete-account-title" className="text-xl font-bold text-gray-900">
                Delete your account?
              </h2>
              <button
                type="button"
                onClick={closeModal}
                disabled={deleting}
                aria-label="Close"
                className="text-gray-500 hover:text-gray-900"
              >
                <FiX size={22} />
              </button>
            </div>

            <p className="mt-4 text-sm leading-6 text-gray-600">
              Your account will not be deleted immediately. You have{" "}
              <strong>45 days</strong> to change your mind. Log in before the
              deadline to cancel deletion.
            </p>

            <form onSubmit={handleDeleteRequest} className="mt-5 space-y-4">
              <div>
                <label htmlFor="delete-password" className="mb-2 block text-sm font-medium">
                  Confirm your password
                </label>
                <input
                  id="delete-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-red-500"
                  placeholder="Enter your password"
                />
              </div>

              {error && (
                <p role="alert" className="text-sm text-red-600">{error}</p>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={deleting}
                  className="flex-1 rounded-xl border border-gray-300 px-4 py-3 font-medium"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={deleting || !password}
                  className="flex-1 rounded-xl bg-red-600 px-4 py-3 font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? "Processing..." : "Confirm Deletion"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
